// TODO: vitest component test (pages are covered by Playwright e2e)
import { Link, useParams, useNavigate, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ArrowLeft } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Skeleton } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { useInvoice } from '@/hooks/useInvoices'
import { useAuth } from '@/hooks/useAuth'
import { InvoiceFields } from '@/components/invoices/InvoiceFields'
import type { Invoice } from '@/types'

/**
 * InvoiceFormPage
 *
 * Thin routing shell that:
 * - Reads URL params: :id (edit), ?lease_id= (pre-select), ?customer_id= (external parker)
 * - Fetches the existing invoice when editing
 * - Redirects to /invoices/:id when saved
 * - Delegates all form rendering to <InvoiceFields>
 *
 * T149-022 (LSE-7): an invoice with payments (amount_paid > 0) is no longer
 * a blanket redirect for every role. The owner still reaches this page and
 * gets a restricted form (dates + billing period only, InvoiceFields'
 * restrictedMode); anyone else sees an inline explanation instead of a
 * silent bounce back to the detail page.
 */
export function InvoiceFormPage() {
  const { t } = useTranslation('invoices')
  const { user } = useAuth()
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const invoiceId = Number(id)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // ?lease_id= — pre-selects the lease dropdown in InvoiceFields
  const leaseIdParam = searchParams.get('lease_id')
  const defaultLeaseId = leaseIdParam ? Number(leaseIdParam) : undefined

  // ?customer_id= — external parker flow (invoice without a lease)
  const customerIdParam = searchParams.get('customer_id')
  const externalCustomerId =
    customerIdParam && !leaseIdParam ? Number(customerIdParam) : undefined

  // EP-130 (T130-014, GAP-1) — the deposit-settlement "Bill shortfall"
  // deep-link: ?item_type=&description=&amount= pre-fills one invoice line
  // (description arrives already URI-encoded; URLSearchParams decodes it).
  const itemTypeParam = searchParams.get('item_type')
  const descriptionParam = searchParams.get('description')
  const amountParam = searchParams.get('amount')
  const prefillItem =
    itemTypeParam && descriptionParam && amountParam
      ? { item_type: itemTypeParam, description: descriptionParam, amount: Number(amountParam) }
      : undefined

  const { data: existingInvoice, isLoading: isLoadingInvoice } = useInvoice(
    isEditing ? invoiceId : 0,
  )

  // T149-022 (LSE-7 decision A): on an invoice with any payment, only the
  // owner may edit it, and even the owner may change only billed_at/due_at/
  // billing_period (InvoiceFields' restrictedMode). Everyone else gets an
  // inline explanation, not a redirect.
  const isPaid = isEditing && (existingInvoice?.amount_paid ?? 0) > 0
  const isOwner = user?.role === 'owner'
  const showRestrictedExplanation = isPaid && !isOwner

  const handleSave = (saved: Invoice) => {
    navigate(`/invoices/${saved.id}`)
  }

  if (isEditing && isLoadingInvoice) {
    return (
      <div className="space-y-6">
        <Card>
          <Skeleton className="h-64" />
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('title'), href: '/invoices' },
          { label: isEditing ? t('form.title_edit') : t('form.title_new') },
        ]}
      />

      <PageHeader
        title={isEditing ? t('form.title_edit') : t('form.title_new')}
        description={isEditing ? t('form.description_edit') : t('form.description_new')}
      />

      {showRestrictedExplanation ? (
        <Card>
          <Alert variant="info">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>{t('messages.cannot_edit_paid')}</span>
              <Button
                as={Link}
                to={`/invoices/${id}`}
                variant="ghost"
                size="sm"
                leftIcon={<ArrowLeft className="w-4 h-4" />}
              >
                {t('actions.back_to_invoice')}
              </Button>
            </div>
          </Alert>
        </Card>
      ) : (
        <InvoiceFields
          invoice={isEditing ? (existingInvoice ?? null) : null}
          defaultLeaseId={defaultLeaseId}
          externalCustomerId={externalCustomerId}
          prefillItem={prefillItem}
          restrictedMode={isPaid}
          onSave={handleSave}
        />
      )}
    </div>
  )
}
